import { useCallback, useEffect, useRef, useState } from 'react';
import { useUpdateEffect } from 'ahooks';
import { isNotNilOrEmpty } from 'ramda-adjunct';
import { equals, map, length, pathOr, prop, propOr, find, fromPairs, props, filter, isNotNil, propEq } from 'ramda';
import moment from 'moment';
import { fetchQuery, useRelayEnvironment } from 'react-relay';
import { PLACEMENT_BLOCK_LABELS, DOCUMENT_HISTORY_PAGE_SIZE } from '../_CONST';
import { ENTRY_KIND, ACTIONS, TARGET_TYPES, MILESTONE_TYPES } from '../../shared/documentHistoriesConstants';
import { QDocumentHistoriesQuery } from '../_graphql/queries/QDocumentHistories';

const mapMilestoneEntry = (historyEntry, baseEntry) => {
  const { savedAt } = baseEntry;
  const milestoneType = prop('milestoneType', historyEntry);

  if (equals(milestoneType, prop('OFFER_VERSION_CREATED', MILESTONE_TYPES))) {
    const offerVersion = prop('offerVersion', historyEntry);
    const offerName = prop('offerName', historyEntry);
    const sourceOfferVersion = prop('sourceOfferVersion', historyEntry);
    const milestoneLabel = `Création de la version ${offerVersion} de l'offre: "${offerName}"`;

    return {
      ...baseEntry,
      milestoneType,
      offerVersion,
      offerName,
      sourceOfferVersion,
      milestoneLabel
    };
  }

  if (equals(milestoneType, prop('HISTORY_START', MILESTONE_TYPES))) {
    const formattedDate = moment(savedAt).format('D MMMM YYYY');
    const milestoneLabel = `L'historique des modifications est disponible à partir du ${formattedDate}`;
    return {
      ...baseEntry,
      milestoneType,
      milestoneLabel
    };
  }

  return null;
};

const enrichWidgetEventEntry = (historyEntry, entry) => {
  const widget = propOr({}, 'widget', historyEntry);
  const placements = map(({ sectionLabel, block, isOrigin }) => ({ isOrigin, locationLabel: `${sectionLabel} - ${PLACEMENT_BLOCK_LABELS[block]}` }), propOr([], 'placements', widget));
  const originPlacement = find(propEq(true, 'isOrigin'), placements);

  const enrichedEntry = {
    ...entry,
    widget: { ...widget, placements },
    isExternal: pathOr(false, ['widget', 'isExternal'], historyEntry),
    // The badge displays additional placements, so the origin placement is excluded.
    placementsCount: length(filter(propEq(false, 'isOrigin'), placements))
  };

  if (!originPlacement) return enrichedEntry;

  const locationLabel = prop('locationLabel', originPlacement);
  return {
    ...enrichedEntry,
    locationLabel
  };
};

const mapEventEntry = (historyEntry, baseEntry) => {
  const { action, targetType, targetName } = historyEntry;
  let entry = {
    action,
    targetType,
    targetName,
    author: propOr({ firstName: '', lastName: '' }, 'author', historyEntry)
  };

  if (equals(action, prop('NAME_UPDATE', ACTIONS))) {
    entry.previousName = prop('previousName', historyEntry);
  } else if (equals(action, prop('DUPLICATED_FROM', ACTIONS))) {
    entry.sourceOfferLabel = prop('sourceOfferLabel', historyEntry);
  }

  if (equals(targetType, prop('WIDGET', TARGET_TYPES))) {
    entry = enrichWidgetEventEntry(historyEntry, entry);
  }

  return {
    ...baseEntry,
    ...entry
  };
};

const mapHistoryEntry = (historyEntry = {}) => {
  const { id, entryKind, savedAt } = historyEntry;
  const baseEntry = {
    id, entryKind, savedAt 
  };

  if (equals(entryKind, prop('MILESTONE', ENTRY_KIND))) return mapMilestoneEntry(historyEntry, baseEntry);
  if (equals(entryKind, prop('EVENT', ENTRY_KIND))) return mapEventEntry(historyEntry, baseEntry);

  return null;
};

const buildFilterVariable = ({ search, targetType, period }) => {
  const hasSearch = isNotNilOrEmpty(search);
  const hasTargetType = isNotNilOrEmpty(targetType);
  const hasPeriod = isNotNilOrEmpty(period) && !equals(period, 'ALL');

  if (!hasSearch && !hasTargetType && !hasPeriod) return undefined;

  const filterVar = {};
  if (hasSearch) filterVar.search = search;
  if (hasTargetType) filterVar.targetType = targetType;
  if (hasPeriod) filterVar.period = period;

  return filterVar;
};

// `search` must already be debounced by the caller (see SearchBar's `debounceWait`).
// Debouncing it here instead would desynchronise it from targetType/period/sort and
// fire an extra request carrying a stale search term whenever several filters change
// in the same user action (e.g. "reset filters").
const useDocumentHistoryEntries = (documentId, {
  search,
  targetType,
  period,
  sort
} = {}) => {
  const environment = useRelayEnvironment();
  const failedPageRef = useRef(null);
  const inFlightRef = useRef(false);
  const pageRef = useRef(0);
  const mountedRef = useRef(true);
  // Incremented on every reset (filter/search/sort/documentId change). Each fetch
  // snapshots it, so a response can tell whether it still belongs to the current filters.
  const requestIdRef = useRef(0);
  const [entries, setEntries] = useState([]);
  const [monthlyCounts, setMonthlyCounts] = useState({});
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const resetPaginationState = useCallback(() => {
    requestIdRef.current += 1;
    failedPageRef.current = null;
    inFlightRef.current = false;
    pageRef.current = 0;
    setEntries([]);
    setMonthlyCounts({});
    setHasMore(true);
    setError(null);
    setIsLoadingMore(false);
  }, []);

  useEffect(() => resetPaginationState(), [documentId, resetPaginationState]);

  const fetchPage = useCallback(async (nextPage, { force = false } = {}) => {
    if (!documentId) return false;
    if (inFlightRef.current) return false;
    if (!force && nextPage <= pageRef.current) return false;

    // A response whose generation is stale belongs to a superseded set of filters
    // (typically the search term that was just cleared): discard it entirely rather
    // than let it overwrite the results of the newer request.
    const requestId = requestIdRef.current;
    const isCurrentRequest = () => equals(requestId, requestIdRef.current);

    inFlightRef.current = true;
    setIsLoadingMore(true);
    setError(null);

    const filterVar = buildFilterVariable({
      search,
      targetType,
      period
    });

    try {
      const variables = {
        documentId,
        pagination: {
          page: nextPage,
          limit: DOCUMENT_HISTORY_PAGE_SIZE
        },
        sort
      };
      if (filterVar !== undefined) variables.filter = filterVar;

      const { documentHistories } = await fetchQuery(environment, QDocumentHistoriesQuery, variables).toPromise();
      if (!mountedRef.current || !isCurrentRequest()) return false;
      const { histories, pagination, monthlyCounts: rawMonthlyCounts } = documentHistories;
      const normalizedEntries = filter(isNotNil, map(mapHistoryEntry, histories));
      const hasNextPage = prop('hasNextPage', pagination);
      const isFirstPage = equals(nextPage, 1);
      const monthlyCountsMap = fromPairs(map(props(['month', 'count']), rawMonthlyCounts || []));
      setEntries(prevEntries => (isFirstPage ? normalizedEntries : [...prevEntries, ...normalizedEntries]));

      // monthlyCounts is a global aggregate independent of pagination,
      // so we only set it once on the first page load.
      if (isFirstPage) setMonthlyCounts(monthlyCountsMap);
      pageRef.current = nextPage;
      setHasMore(hasNextPage);
      failedPageRef.current = null;
      return true;
    } catch (fetchError) {
      if (!mountedRef.current || !isCurrentRequest()) return false;

      failedPageRef.current = nextPage;
      setError(fetchError);
      return false;
    } finally {
      // Only the current request may clear the shared in-flight/loading state: a stale
      // request clearing it would let a third request start concurrently.
      if (isCurrentRequest()) {
        if (mountedRef.current) setIsLoadingMore(false);
        inFlightRef.current = false;
      }
    }
  }, [documentId, environment, targetType, period, sort, search]);

  const resetAndFetch = useCallback(() => {
    resetPaginationState();
    fetchPage(1, { force: true });
  }, [fetchPage, resetPaginationState]);

  useUpdateEffect(() => resetAndFetch(), [targetType, period, sort, search, resetAndFetch]);

  const loadMore = useCallback(async () => {
    if (!hasMore) return false;
    return fetchPage(pageRef.current + 1);
  }, [fetchPage, hasMore]);

  const retry = useCallback(() => {
    const pageToRetry = failedPageRef.current || (pageRef.current + 1);
    if (!pageToRetry) return;

    setError(null);
    fetchPage(pageToRetry, { force: true });
  }, [fetchPage]);

  return {
    entries,
    monthlyCounts,
    isLoadingMore,
    hasMore,
    error,
    fetchPage,
    loadMore,
    retry
  };
};

export default useDocumentHistoryEntries;
